import { Injectable } from '@angular/core';
import { AbstractControl, AsyncValidatorFn, FormBuilder, FormControl, FormGroup, ValidationErrors, ValidatorFn, Validators } from "@angular/forms";
import { CustomValidators } from "@shared/customValidators";
import { Contact, ContactRole, FatcaChoice } from '@shared/models/data-model';
import { resolveAboradCode } from '@shared/services/fiscal-code-abroad-codes';
import { FiscalCodeService } from '@shared/services/fiscal-code.service';
import moment from 'moment-mini';
import { NGXLogger } from 'ngx-logger';
import { Address3HelperService, AddressFormGroup } from '@shared/components/address3/address3-helper.service';

export interface PersonFormGroup {
    contactId: FormControl<string>,
    firstName: FormControl<string>;
    lastName: FormControl<string>;
    nationality: FormControl<string>;
    dateOfBirth?: FormControl<string>;
    codiceFiscale?: FormControl<string>;
    email: FormControl<string>;
    mobile: FormGroup<{
        prefix: FormControl<string>;
        number: FormControl<string>;
    }>;
    address: FormGroup<AddressFormGroup>;
    isFatcaSubject?: FormControl<FatcaChoice>;
    roles: FormControl<ContactRole[]>;
}

@Injectable({
    providedIn: 'root'
})
export class PersonFormHelperService {
    public defaultValidators: { [k in keyof PersonFormGroup]?: ValidatorFn[] } = {
        firstName: [
            Validators.required,
            this.codiceFiscaleFirstNameValidatorFactory(),
            Validators.maxLength(CustomValidators.getContactMaxlength("firstName"))
        ],
        lastName: [
            Validators.required,
            this.codiceFiscaleLastNameValidatorFactory(),
            Validators.maxLength(CustomValidators.getContactMaxlength("lastName"))
        ],
        dateOfBirth: [Validators.required, CustomValidators.dateValidatorFactory(18, 120)],
        codiceFiscale: [Validators.required],
        nationality: [Validators.required],
        email: [
            Validators.required,
            CustomValidators.emailValidatorFactory(),
            Validators.maxLength(CustomValidators.getContactMaxlength("email")),
            CustomValidators.noSoldoPecAddressValidatorFactory()
        ],
        mobile: [CustomValidators.phoneValidatorFactory('prefix', 'number', true)],
        isFatcaSubject: [Validators.required]
    };

    public asyncValidators: { [k in keyof PersonFormGroup]?: AsyncValidatorFn[] } = {
        codiceFiscale: []
    }

    constructor(
        private readonly fb: FormBuilder,
        private readonly fiscalCodeService: FiscalCodeService,
        private readonly logger: NGXLogger,
        private readonly addressFormHelper: Address3HelperService) {

        this.asyncValidators.codiceFiscale.push(this.fiscalCodeService.validator);
    }

    createEmptyPersonFormGroup(countryOfIncorporation: string): FormGroup<PersonFormGroup> {
        const formGroup: FormGroup<PersonFormGroup> = this.fb.group({
            contactId: [null],
            firstName: [null],
            lastName: [null],
            nationality: [countryOfIncorporation],
            email: [null],
            mobile: this.fb.group({
                prefix: [null],
                number: [null]
            }),
            address: this.addressFormHelper.createEmptyAddressFormGroup(countryOfIncorporation),
            roles: [null]
        });

        this.addOrRemoveDateOfBirthAndCodiceFiscale(formGroup, countryOfIncorporation);
        this.addOrRemoveFatcaSubjectQuestion(formGroup);

        return formGroup;
    }

    public createPersonFormGroupForContact(person: Contact, countryOfIncorporation: string): FormGroup<PersonFormGroup> {
        const { number, icc } = this.interpretMobileNumber(person.mobilePhone, person.mobilePhoneICC);

        const formGroup: FormGroup<PersonFormGroup> = this.fb.group({
            contactId: [person.id],
            firstName: [person.firstName],
            lastName: [person.lastName],
            nationality: [person.citizenshipCountryISO || countryOfIncorporation],
            email: [person.email],
            mobile: this.fb.group({
                prefix: [icc],
                number: [number]
            }),
            address: this.addressFormHelper.createPrepopulatedAddressFormGroup(person.address ?? { countryISO: countryOfIncorporation }),
            roles: [person.roles]
        });

        this.addOrRemoveDateOfBirthAndCodiceFiscale(formGroup, countryOfIncorporation, person);
        this.addOrRemoveFatcaSubjectQuestion(formGroup, person);

        return formGroup;
    }

    interpretMobileNumber(fullNumber: string, icc: string): { icc: string; number: string } {
        let numberOnly = fullNumber;
        if (numberOnly?.includes(icc)) {
            numberOnly = numberOnly.substring(icc.length);
        }
        return {
            icc: icc,
            number: numberOnly
        };
    }

    applyDefaultValidators(form: FormGroup<PersonFormGroup>): void {
        for (const [key, control] of Object.entries(form.controls)) {
            const c = (control as AbstractControl);
            if (!this.defaultValidators[key]) {
                continue;
            }
            this.addDefaultValidators(key, c);
            if (!this.asyncValidators[key]) {
                continue;
            }
            this.addDefaultAsyncValidators(key, c);
        }
    }

    private addDefaultAsyncValidators(key: string, c: AbstractControl<any, any>) {
        for (const v of this.asyncValidators[key]) {
            if (c.enabled && !c.hasAsyncValidator(v)) {
                c.addAsyncValidators(v);
            }
        }
    }

    private addDefaultValidators(key: string, c: AbstractControl<any, any>) {
        for (const v of this.defaultValidators[key]) {
            if (c.enabled && !c.hasValidator(v)) {
                c.addValidators(v);
            }
        }
    }

    applyUniquenessValidators(form: FormGroup<PersonFormGroup>, contactsSet: Contact[]): void {
        const uniqueEmailValidator = this.uniqueEmailValidatorFactory(form.value.contactId, contactsSet);
        const uniqueMobileValidator = this.uniqueMobileNumberValidatorFactory(form.value.contactId, contactsSet);
        form.controls.email.addValidators(uniqueEmailValidator);
        form.controls.mobile.addValidators(uniqueMobileValidator);

        if (form.controls.codiceFiscale) {
            const uniqueCodiceFiscaleValidator = this.uniqueCodiceFiscaleValidatorFactory(form.value.contactId, contactsSet);
            form.controls.codiceFiscale.addValidators(uniqueCodiceFiscaleValidator);
        }
    }

    addOrRemoveFatcaSubjectQuestion(personFormGroup: FormGroup<PersonFormGroup>, person: Contact = null): void {
        // Read the leaf controls directly rather than personFormGroup.value: when this runs from a
        // child control's valueChanges (e.g. nationality), the parent group's cached .value snapshot
        // is not updated yet, so .value would still hold the previous nationality/country.
        const nationality = personFormGroup.controls.nationality.value;
        const residenceCountry = personFormGroup.controls.address.controls.countryISO.value;
        const usCitizenOrResident = [nationality, residenceCountry].includes('USA');

        if (!usCitizenOrResident && !personFormGroup.controls.isFatcaSubject) {
            personFormGroup.addControl("isFatcaSubject", new FormControl(person?.fatcaSubject));
            personFormGroup.controls.isFatcaSubject.updateValueAndValidity();
        }

        if (usCitizenOrResident && personFormGroup.controls.isFatcaSubject) {
            personFormGroup.removeControl("isFatcaSubject");
        }
    }

    addOrRemoveDateOfBirthAndCodiceFiscale(personFormGroup: FormGroup<PersonFormGroup>, countryOfIncorporation: string, person: Contact = null): void {
        const mustProvideCodiceFiscale = [countryOfIncorporation, personFormGroup.value.address.countryISO].includes("ITA");

        if (mustProvideCodiceFiscale && !personFormGroup.controls.codiceFiscale) {
            personFormGroup.removeControl("dateOfBirth");
            personFormGroup.addControl("codiceFiscale", new FormControl<string>(person?.fiscalCode));
            personFormGroup.controls.codiceFiscale.statusChanges.subscribe(() => {
                personFormGroup.controls.firstName.updateValueAndValidity();
                personFormGroup.controls.firstName.markAsTouched();
                personFormGroup.controls.lastName.updateValueAndValidity();
                personFormGroup.controls.lastName.markAsTouched();
            });
            personFormGroup.controls.codiceFiscale.updateValueAndValidity();
        }

        if (!mustProvideCodiceFiscale && !personFormGroup.controls.dateOfBirth) {
            personFormGroup.removeControl("codiceFiscale");
            const formattedBirthDate = person?.birthDate ? moment(person.birthDate, 'YYYY-MM-DD').format('DD/MM/YYYY') : null;
            personFormGroup.addControl("dateOfBirth", new FormControl<string>(formattedBirthDate));
        }
    }

    async mapFormValuesToContact(personFormGroup: FormGroup<PersonFormGroup>): Promise<Contact> {
        const values = personFormGroup.value;
        const isUsCitizenOrResident = [values.address.countryISO, values.nationality].includes("USA");

        const cfValues = {
            birthPlace: null,
            dateOfBirth: ''
        };

        let codiceFiscaleGender: "M" | "F" = null;
        let codiceFiscaleBirthCountry = null;

        if (values.codiceFiscale) {
            codiceFiscaleBirthCountry = "ITA";
            cfValues.birthPlace = await this.fiscalCodeService.retrieveBirthPlace(values.codiceFiscale);

            if (!cfValues.birthPlace) {
                const townCode = this.fiscalCodeService.extractTownCode(values.codiceFiscale);
                const abroadCountry = resolveAboradCode(townCode);

                codiceFiscaleBirthCountry = abroadCountry ?? "ITA";

                cfValues.birthPlace = {
                    name: '',
                    province: ''
                };
            }

            cfValues.dateOfBirth = moment(FiscalCodeService.retrieveBirthDate(values.codiceFiscale)).format('DD/MM/YYYY');
            codiceFiscaleGender = FiscalCodeService.refetrieveSex(values.codiceFiscale);
        }

        return {
            id: values.contactId,
            firstName: values.firstName,
            lastName: values.lastName,
            fiscalCode: values.codiceFiscale?.toUpperCase() || '',
            gender: codiceFiscaleGender,
            birthPlace: cfValues.birthPlace?.name,
            birthProvince: cfValues.birthPlace?.province,
            birthCountry: codiceFiscaleBirthCountry,
            citizenshipCountryISO: values.nationality,
            birthDate: moment(values.dateOfBirth || cfValues.dateOfBirth, 'DD/MM/YYYY').format('YYYY-MM-DD'),
            mobilePhoneICC: values.mobile.prefix,
            mobilePhone: `${values.mobile.prefix}${values.mobile.number}`,
            email: values.email,
            address: {
                line1: values.address.line1,
                line2: values.address.line2,
                city: values.address.city,
                postalCode: values.address.postalCode,
                countryISO: values.address.countryISO,
                province: values.address.province,
                searchRefCode: values.address.searchRefCode
            },
            fatcaSubject: isUsCitizenOrResident ? FatcaChoice.Yes : values.isFatcaSubject,
            roles: values.roles
        };
    }

    codiceFiscaleFirstNameValidatorFactory(): ValidatorFn {
        return (firstNameControl: AbstractControl): ValidationErrors => {
            const cfField = (firstNameControl.parent as FormGroup<PersonFormGroup>)?.controls.codiceFiscale;
            if (firstNameControl.value && cfField?.value && cfField?.valid) {
                if (!this.fiscalCodeService.validateAgainstFirstName(cfField.value, firstNameControl.value)) {
                    return {
                        "firstNameCfMismatch": true
                    };
                }
            }
            return null;
        };
    }

    codiceFiscaleLastNameValidatorFactory(): ValidatorFn {
        return (lastNameControl: AbstractControl): ValidationErrors => {
            const cfField = (lastNameControl.parent as FormGroup<PersonFormGroup>)?.controls.codiceFiscale;
            if (lastNameControl.value && cfField?.value && cfField?.valid) {
                if (!this.fiscalCodeService.validateAgainstLastName(cfField.value, lastNameControl.value)) {
                    return {
                        "lastNameCfMismatch": true
                    };
                }
            }
            return null;
        };
    }

    uniqueMobileNumberValidatorFactory(contactId: string, allContacts: Contact[]): ValidatorFn {
        return (control: AbstractControl) => {
            const controlMobileNumber = control.value.prefix + control.value.number;
            const others = allContacts.filter(c => c.id !== contactId && this.hasSomeRole(c));

            if (others.some(c => c.mobilePhone === controlMobileNumber)) {
                return {
                    duplicated: true
                };
            }
            return null;
        };
    }

    uniqueEmailValidatorFactory(contactId: string, allContacts: Contact[]): ValidatorFn {
        return (control: AbstractControl) => {
            const controlEmail = control.value;
            const others = allContacts.filter(c => c.id !== contactId && this.hasSomeRole(c));

            if (others.some(c => c.email?.toLowerCase() === controlEmail?.toLowerCase())) {
                return {
                    noDuplicatedEmail: true
                };
            }
            return null;
        };
    }

    uniqueCodiceFiscaleValidatorFactory(contactId: string, allContacts: Contact[]): ValidatorFn {
        return (control: AbstractControl) => {
            const controlCodiceFiscale = control.value;
            const others = allContacts.filter(c => c.id !== contactId && this.hasSomeRole(c));

            if (others.some(c => c.fiscalCode?.toLowerCase() === controlCodiceFiscale?.toLowerCase())) {
                return {
                    duplicated: true
                };
            }
            return null;
        };
    }

    hasSomeRole(c: Contact): boolean {
        return c.roles.length > 0;
    }
}
